import cronstrue from "cronstrue";
import { CronExpressionParser } from "cron-parser";
import "cronstrue/locales/zh_CN";

import type { CronFlavor, Locale } from "./types";

export interface CronAnalysis {
  normalizedExpression: string;
  description: string;
  nextRuns: string[];
  timeZone: string;
}

export function analyzeCronExpression(
  input: string,
  flavor: CronFlavor,
  locale: Locale,
  nextCount = 7
): CronAnalysis {
  const normalized = normalizeCronExpression(input, flavor);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  validateFieldCount(normalized, flavor);

  try {
    const interval = CronExpressionParser.parse(normalized, {
      currentDate: new Date(),
      tz: timeZone
    });
    const nextRuns: string[] = [];

    for (let index = 0; index < nextCount; index += 1) {
      nextRuns.push(formatCronRun(interval.next().toDate(), timeZone));
    }

    return {
      normalizedExpression: normalized,
      description: cronstrue.toString(normalized, {
        locale: locale === "zh" ? "zh_CN" : "en",
        use24HourTimeFormat: true,
        verbose: true,
        throwExceptionOnParseError: true
      }),
      nextRuns,
      timeZone
    };
  } catch {
    throw new Error("INVALID_CRON_EXPRESSION");
  }
}

export function detectCronFlavor(input: string): CronFlavor {
  const normalized = input.trim();

  if (normalized === "") {
    return "linux";
  }

  const fields = normalized.split(/\s+/);

  if (
    normalized.includes("?") ||
    normalized.includes("L") ||
    normalized.includes("#")
  ) {
    return "quartz";
  }

  if (fields.length >= 6) {
    return "spring";
  }

  return "linux";
}

function normalizeCronExpression(input: string, flavor: CronFlavor): string {
  const normalized = input.trim();

  if (normalized === "") {
    throw new Error("INVALID_CRON_EXPRESSION");
  }

  if (flavor !== "linux") {
    return normalized;
  }

  switch (normalized) {
    case "@yearly":
    case "@annually":
      return "0 0 1 1 *";
    case "@monthly":
      return "0 0 1 * *";
    case "@weekly":
      return "0 0 * * 0";
    case "@daily":
    case "@midnight":
      return "0 0 * * *";
    case "@hourly":
      return "0 * * * *";
    default:
      return normalized;
  }
}

function validateFieldCount(expression: string, flavor: CronFlavor): void {
  const count = expression.split(/\s+/).length;

  if (flavor === "linux" && count === 5) {
    return;
  }

  if (flavor === "spring" && count === 6) {
    return;
  }

  if (flavor === "quartz" && (count === 6 || count === 7)) {
    return;
  }

  throw new Error("INVALID_CRON_EXPRESSION");
}

function formatCronRun(date: Date, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  });
  const values = new Map<string, string>();

  for (const part of formatter.formatToParts(date)) {
    if (
      part.type === "year" ||
      part.type === "month" ||
      part.type === "day" ||
      part.type === "hour" ||
      part.type === "minute" ||
      part.type === "second"
    ) {
      values.set(part.type, part.value);
    }
  }

  return `${values.get("year")}-${values.get("month")}-${values.get("day")} ${values.get("hour")}:${values.get("minute")}:${values.get("second")}`;
}
